import { useEffect, useState, useCallback } from "react";
import {
  Tent, Star, Plus, Pencil, Trash2, X, Search, MapPin, Settings2,
  Filter, Check, Loader2,
} from "lucide-react";
import api from "../../api/axios";

const STATUS_OPTIONS = ["Affected", "Not Affected", "Custom"];
const todayStr = () => new Date().toISOString().split("T")[0];
const nowTime = () => new Date().toTimeString().slice(0, 5);

const emptyForm = () => ({
  name: "", age: "", date_of_birth: "", mobile: "",
  camp_location: "", camp_date: todayStr(), camp_time: nowTime(),
  status: "Not Affected", status_custom: "", custom: {}, favourite: false,
});

export default function Camp() {
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  const [filters, setFilters] = useState({ camp_location: "", date_from: "", date_to: "", q: "", favourite: false, custom: {} });

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [newLoc, setNewLoc] = useState("");
  const [saving, setSaving] = useState(false);

  const [showFields, setShowFields] = useState(false);
  const [showLocations, setShowLocations] = useState(false);

  const flash = (type, text) => { setBanner({ type, text }); setTimeout(() => setBanner(null), 3000); };

  const loadMeta = useCallback(async () => {
    const [l, f] = await Promise.all([api.get("/api/camp-locations"), api.get("/api/camp-fields")]);
    setLocations(l.data.locations || []);
    setFields(f.data.fields || []);
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filters.camp_location) params.camp_location = filters.camp_location;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.q) params.q = filters.q;
    if (filters.favourite) params.favourite = "1";
    Object.entries(filters.custom || {}).forEach(([k, v]) => { if (v) params[`cf_${k}`] = v; });
    const res = await api.get("/api/camp-entries", { params });
    setEntries(res.data.entries || []);
    setLoading(false);
  }, [filters]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const openAdd = () => { setEditId(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      name: e.name || "", age: e.age || "", date_of_birth: e.date_of_birth || "", mobile: e.mobile || "",
      camp_location: e.camp_location || "", camp_date: e.camp_date || todayStr(), camp_time: e.camp_time || nowTime(),
      status: e.status || "Not Affected", status_custom: e.status_custom || "", custom: e.custom || {}, favourite: !!e.favourite,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { flash("error", "Name is required."); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/api/camp-entries/${editId}`, form);
      else await api.post("/api/camp-entries", form);
      setShowForm(false);
      flash("success", editId ? "Entry updated." : "Entry added.");
      loadEntries();
    } catch (err) {
      flash("error", err.response?.data?.message || "Failed to save.");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this camp entry?")) return;
    await api.delete(`/api/camp-entries/${id}`);
    flash("success", "Entry deleted."); loadEntries();
  };

  const toggleFav = async (e) => {
    await api.patch(`/api/camp-entries/${e.id}/favourite`, { favourite: !e.favourite });
    loadEntries();
  };

  const addLocationInline = async () => {
    const name = newLoc.trim(); if (!name) return;
    const res = await api.post("/api/camp-locations", { name });
    setNewLoc(""); await loadMeta();
    setForm((f) => ({ ...f, camp_location: res.data.location.name }));
  };

  const filterFields = fields.filter((f) => f.in_filter);

  return (
    <div className="mx-auto max-w-7xl" data-testid="camp-page">
      {banner && (
        <div className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${banner.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="camp-banner">
          {banner.text}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-teal-600 to-emerald-500 grid place-items-center text-white shadow-md">
            <Tent size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Camp Screening</h1>
            <p className="text-sm text-slate-500">Eye camp entries, locations & custom fields</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowLocations(true)} data-testid="camp-manage-locations" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <MapPin size={16} /> Locations
          </button>
          <button onClick={() => setShowFields(true)} data-testid="camp-manage-fields" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Settings2 size={16} /> Custom Fields
          </button>
          <button onClick={openAdd} data-testid="camp-add-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-teal-700">
            <Plus size={16} /> Add Customer
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Filter size={13} /> Filters</div>
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Fld label="Location">
            <select value={filters.camp_location} onChange={(e) => setFilters({ ...filters, camp_location: e.target.value })} data-testid="camp-filter-location" className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm outline-none focus:border-teal-500">
              <option value="">All Locations</option>
              {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
            </select>
          </Fld>
          <Fld label="Search">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Name / mobile" data-testid="camp-filter-search" className="h-10 w-full rounded-md border border-slate-300 pl-8 pr-2 text-sm outline-none focus:border-teal-500" />
            </div>
          </Fld>
          <Fld label="From">
            <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="camp-filter-from" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm outline-none focus:border-teal-500" />
          </Fld>
          <Fld label="To">
            <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="camp-filter-to" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm outline-none focus:border-teal-500" />
          </Fld>
          <Fld label="Show">
            <button onClick={() => setFilters({ ...filters, favourite: !filters.favourite })} data-testid="camp-filter-fav" className={`h-10 w-full rounded-md border px-2 text-sm font-medium inline-flex items-center justify-center gap-1.5 ${filters.favourite ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-300 bg-white text-slate-600"}`}>
              <Star size={15} className={filters.favourite ? "fill-amber-400 text-amber-400" : ""} /> Favourites
            </button>
          </Fld>
        </div>
        {filterFields.length > 0 && (
          <div className="mt-3 grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {filterFields.map((f) => (
              <Fld key={f.id} label={f.label}>
                <input placeholder={f.label} value={filters.custom[f.key] || ""} data-testid={`camp-filter-cf-${f.key}`}
                  onChange={(e) => setFilters({ ...filters, custom: { ...filters.custom, [f.key]: e.target.value } })}
                  className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm outline-none focus:border-teal-500" />
              </Fld>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">★</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Age / DOB</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Date / Time</th>
                <th className="px-4 py-3">Status</th>
                {fields.map((f) => <th key={f.id} className="px-4 py-3">{f.label}</th>)}
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={8 + fields.length} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={8 + fields.length} className="px-4 py-10 text-center text-slate-400">No camp entries found.</td></tr>
              ) : entries.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`camp-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleFav(e)} data-testid={`camp-fav-${e.id}`}>
                      <Star size={17} className={e.favourite ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"} />
                    </button>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{e.name}</td>
                  <td className="px-4 py-3 text-slate-600">{e.age || (e.date_of_birth ? e.date_of_birth : "-")}</td>
                  <td className="px-4 py-3 text-slate-600">{e.mobile || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{e.camp_location || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{e.camp_date || "-"}{e.camp_time ? ` · ${e.camp_time}` : ""}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${e.status === "Affected" ? "bg-red-100 text-red-700" : e.status === "Not Affected" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
                      {e.status === "Custom" ? (e.status_custom || "Custom") : e.status}
                    </span>
                  </td>
                  {fields.map((f) => <td key={f.id} className="px-4 py-3 text-slate-600">{(e.custom && e.custom[f.key]) || "-"}</td>)}
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => openEdit(e)} data-testid={`camp-edit-${e.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600"><Pencil size={15} /></button>
                      <button onClick={() => del(e.id)} data-testid={`camp-del-${e.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <Modal title={editId ? "Edit Camp Entry" : "Add Camp Entry"} onClose={() => setShowForm(false)}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Fld label="Camp Location">
              <input
                list="camp-loc-list"
                value={form.camp_location}
                onChange={(e) => setForm({ ...form, camp_location: e.target.value })}
                placeholder="Select or type location"
                data-testid="camp-form-location"
                autoComplete="off"
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
              />
              <datalist id="camp-loc-list">
                {locations.map((l) => <option key={l.id} value={l.name} />)}
              </datalist>
              <div className="mt-1.5 flex gap-2">
                <input value={newLoc} onChange={(e) => setNewLoc(e.target.value)} placeholder="+ New location" data-testid="camp-form-newloc" className="h-9 flex-1 rounded-md border border-dashed border-slate-300 px-2 text-sm" />
                <button onClick={addLocationInline} className="rounded-md bg-slate-800 px-3 text-xs font-medium text-white">Create</button>
              </div>
            </Fld>
            <div className="grid grid-cols-2 gap-2">
              <Fld label="Date"><input type="date" value={form.camp_date} onChange={(e) => setForm({ ...form, camp_date: e.target.value })} data-testid="camp-form-date" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm" /></Fld>
              <Fld label="Time"><input type="time" value={form.camp_time} onChange={(e) => setForm({ ...form, camp_time: e.target.value })} data-testid="camp-form-time" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm" /></Fld>
            </div>
            <Fld label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="camp-form-name" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm" /></Fld>
            <div className="grid grid-cols-2 gap-2">
              <Fld label="Age"><input value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="e.g. 45" data-testid="camp-form-age" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm" /></Fld>
              <Fld label="or Date of Birth"><input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="camp-form-dob" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm" /></Fld>
            </div>
            <Fld label="Mobile No."><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="camp-form-mobile" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm" /></Fld>
            <Fld label="Status">
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button key={s} onClick={() => setForm({ ...form, status: s })} data-testid={`camp-form-status-${s.replace(/\s/g, "")}`}
                    className={`rounded-md border px-3 py-1.5 text-sm ${form.status === s ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-300 bg-white text-slate-600"}`}>{s}</button>
                ))}
              </div>
              {form.status === "Custom" && (
                <input value={form.status_custom} onChange={(e) => setForm({ ...form, status_custom: e.target.value })} placeholder="Enter custom status" data-testid="camp-form-status-custom" className="mt-2 h-10 w-full rounded-md border border-slate-300 px-2 text-sm" />
              )}
            </Fld>
          </div>

          {/* Custom fields */}
          {fields.length > 0 && (
            <div className="mt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Additional Fields</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {fields.map((f) => (
                  <Fld key={f.id} label={f.label}>
                    {f.field_type === "select" && f.options?.length ? (
                      <>
                        <input list={`cf-form-${f.key}`} value={form.custom[f.key] || ""} onChange={(e) => setForm({ ...form, custom: { ...form.custom, [f.key]: e.target.value } })} data-testid={`camp-form-cf-${f.key}`} autoComplete="off" className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm" />
                        <datalist id={`cf-form-${f.key}`}>{f.options.map((o) => <option key={o} value={o} />)}</datalist>
                      </>
                    ) : (
                      <input type={f.field_type === "number" ? "number" : "text"} value={form.custom[f.key] || ""} onChange={(e) => setForm({ ...form, custom: { ...form.custom, [f.key]: e.target.value } })} data-testid={`camp-form-cf-${f.key}`} className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm" />
                    )}
                  </Fld>
                ))}
              </div>
            </div>
          )}

          <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.favourite} onChange={(e) => setForm({ ...form, favourite: e.target.checked })} data-testid="camp-form-fav" className="h-4 w-4 accent-amber-500" />
            Mark as favourite
          </label>

          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="camp-form-save" className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
            </button>
          </div>
        </Modal>
      )}

      {showFields && <FieldsManager fields={fields} reload={loadMeta} flash={flash} onClose={() => setShowFields(false)} />}
      {showLocations && <LocationsManager locations={locations} reload={loadMeta} flash={flash} onClose={() => setShowLocations(false)} />}
    </div>
  );
}

const Fld = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
    {children}
  </div>
);

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="camp-modal-close" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FieldsManager({ fields, reload, flash, onClose }) {
  const [label, setLabel] = useState("");
  const [ftype, setFtype] = useState("text");
  const [options, setOptions] = useState("");
  const [inFilter, setInFilter] = useState(false);

  const add = async () => {
    if (!label.trim()) return;
    try {
      await api.post("/api/camp-fields", {
        label: label.trim(), field_type: ftype,
        options: ftype === "select" ? options.split(",").map((s) => s.trim()).filter(Boolean) : [],
        in_filter: inFilter,
      });
      setLabel(""); setOptions(""); setInFilter(false); setFtype("text");
      flash("success", "Field added."); reload();
    } catch (err) { flash("error", err.response?.data?.message || "Failed."); }
  };
  const toggle = async (f) => { await api.put(`/api/camp-fields/${f.id}`, { in_filter: !f.in_filter }); reload(); };
  const del = async (f) => { if (!window.confirm(`Delete field "${f.label}"?`)) return; await api.delete(`/api/camp-fields/${f.id}`); flash("success", "Field deleted."); reload(); };

  return (
    <Modal title="Custom Fields" onClose={onClose}>
      <div className="space-y-2" data-testid="camp-fields-list">
        {fields.length === 0 && <p className="text-sm text-slate-400">No custom fields yet.</p>}
        {fields.map((f) => (
          <div key={f.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-800">{f.label}</p>
              <p className="text-[11px] text-slate-400">{f.field_type}{f.options?.length ? ` · ${f.options.join(", ")}` : ""}</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => toggle(f)} data-testid={`camp-field-filter-${f.key}`} className={`inline-flex items-center gap-1.5 text-xs font-medium ${f.in_filter ? "text-teal-600" : "text-slate-400"}`}>
                <span className={`relative h-5 w-9 rounded-full transition ${f.in_filter ? "bg-teal-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${f.in_filter ? "left-4.5 translate-x-3.5" : "left-0.5"}`} style={{ left: f.in_filter ? "1.125rem" : "0.125rem" }} />
                </span>
                Filter
              </button>
              <button onClick={() => del(f)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Add New Field</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Field label (e.g. Blood Pressure)" data-testid="camp-newfield-label" className="h-10 rounded-md border border-slate-300 px-2 text-sm" />
          <select value={ftype} onChange={(e) => setFtype(e.target.value)} data-testid="camp-newfield-type" className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm">
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="select">Dropdown</option>
          </select>
          {ftype === "select" && (
            <input value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Options comma separated" className="h-10 rounded-md border border-slate-300 px-2 text-sm sm:col-span-2" />
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={inFilter} onChange={(e) => setInFilter(e.target.checked)} data-testid="camp-newfield-infilter" className="h-4 w-4 accent-teal-500" />
            Show in filter / search
          </label>
          <button onClick={add} data-testid="camp-newfield-add" className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-teal-700"><Plus size={15} /> Add Field</button>
        </div>
      </div>
    </Modal>
  );
}

function LocationsManager({ locations, reload, flash, onClose }) {
  const [name, setName] = useState("");
  const add = async () => { if (!name.trim()) return; await api.post("/api/camp-locations", { name: name.trim() }); setName(""); flash("success", "Location added."); reload(); };
  const del = async (l) => { if (!window.confirm(`Delete "${l.name}"?`)) return; await api.delete(`/api/camp-locations/${l.id}`); flash("success", "Location deleted."); reload(); };
  return (
    <Modal title="Camp Locations" onClose={onClose}>
      <div className="space-y-2" data-testid="camp-locations-list">
        {locations.length === 0 && <p className="text-sm text-slate-400">No locations yet.</p>}
        {locations.map((l) => (
          <div key={l.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <span className="text-sm text-slate-800">{l.name}</span>
            <button onClick={() => del(l)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New camp location" data-testid="camp-newloc-input" className="h-10 flex-1 rounded-md border border-slate-300 px-2 text-sm" />
        <button onClick={add} data-testid="camp-newloc-add" className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-teal-700"><Plus size={15} /> Add</button>
      </div>
    </Modal>
  );
}
